const LAST_UPDATED = "2026-10-11";
const SUPPORT_EMAIL = "support@shuwa.app";

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-montserrat text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 border-b border-border pb-6">
        <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
          Last updated {LAST_UPDATED}
        </span>
        <p className="m-0 text-sm text-slate-500">
          Please read these Terms carefully before using SHUWA.
        </p>
      </div>

      <p>
        These Terms of Service ("Terms") govern your use of SHUWA's website
        and mobile apps (the "Service"). By creating an account or booking
        through SHUWA, you agree to these Terms.
      </p>

      <Section title="The Service">
        <p>
          SHUWA lets customers discover and book appointments with service
          businesses, and lets businesses manage bookings, staff, and
          payments. SHUWA is a platform connecting customers and businesses —
          we are not a party to the services a business provides you.
        </p>
      </Section>

      <Section title="Accounts">
        <p>
          You must provide accurate information when creating an account and
          are responsible for keeping your login credentials secure. You
          must be at least 18 years old to use SHUWA.
        </p>
      </Section>

      <Section title="Bookings and payments">
        <ul className="list-disc space-y-2 pl-5 marker:text-primary">
          <li>
            When you book a service, you enter into an agreement with the
            business providing it. SHUWA facilitates the booking and, where
            applicable, the payment.
          </li>
          <li>
            Online payments are processed by our mobile money partners (MTN
            Mobile Money and Airtel Money). By paying through SHUWA, you
            also agree to your mobile money provider's terms of service.
          </li>
          <li>
            Cancellation and refund policies are set by each business and
            shown at the time of booking, unless required otherwise by law.
          </li>
        </ul>
      </Section>

      <Section title="Business accounts">
        <p>
          Businesses using SHUWA are responsible for the accuracy of their
          listing, service, staff, and pricing information, and for
          delivering the services they list. Businesses agree not to use
          SHUWA for unlawful purposes.
        </p>
        <ul className="list-disc space-y-2 pl-5 marker:text-primary">
          <li>
            Do not ask customers to cancel their SHUWA booking and pay you
            directly in order to avoid SHUWA booking fees, as this can get
            your account suspended.
          </li>
        </ul>
      </Section>

      <Section title="Taxes, invoicing and legal compliance">
        <p>
          This section applies to businesses and professionals offering
          services on SHUWA.
        </p>
        <ol className="list-decimal space-y-3 pl-5 marker:font-semibold marker:text-primary">
          <li>
            <span className="font-semibold text-slate-900">
              Professional tax responsibilities.
            </span>{" "}
            By registering on and using SHUWA, you acknowledge and agree
            that you are solely responsible for complying with all tax laws
            and regulations applicable to the services you provide through
            the platform.
          </li>
          <li>
            <span className="font-semibold text-slate-900">
              Tax declaration and payment.
            </span>{" "}
            You are responsible for declaring your income, paying all
            applicable taxes, and fulfilling any registration, reporting or
            other tax obligations arising from your business or professional
            activities.
          </li>
          <li>
            <span className="font-semibold text-slate-900">
              Receipts and tax invoices.
            </span>{" "}
            You are responsible for issuing receipts, EBM tax invoices or
            other legally required transaction documents to customers for
            the services you provide, in accordance with applicable Rwandan
            law.
          </li>
          <li>
            <span className="font-semibold text-slate-900">
              Payments processed through SHUWA.
            </span>{" "}
            Where a customer pays for your services through SHUWA, you
            remain responsible for your applicable tax obligations in
            relation to those services. The fact that SHUWA facilitates
            bookings or payments does not, by itself, transfer your tax
            responsibilities to SHUWA.
          </li>
          <li>
            <span className="font-semibold text-slate-900">
              Professional compliance.
            </span>{" "}
            You agree to provide accurate business and payment information
            and to comply with all applicable laws and regulations. SHUWA
            may request relevant information where reasonably necessary to
            verify compliance or meet its own legal obligations.
          </li>
          <li>
            <span className="font-semibold text-slate-900">SHUWA's role.</span>{" "}
            SHUWA operates as a marketplace facilitating connections and
            bookings between customers and independent service providers.
            Nothing in this section excludes or limits any tax, invoicing or
            other legal obligations that SHUWA itself may have under
            applicable law.
          </li>
        </ol>
      </Section>

      <Section title="Communications">
        <p>
          By booking through SHUWA, you consent to receive booking-related
          messages (confirmations, reminders, updates) via WhatsApp, push
          notification, or email.
        </p>
      </Section>

      <Section title="Acceptable use">
        <p>
          You agree not to misuse the Service — including attempting to
          access it using a method other than the interface and instructions
          we provide, interfering with its operation, or using it to harass,
          defraud, or harm others.
        </p>
      </Section>

      <Section title="Termination">
        <p>
          You may stop using SHUWA and delete your account at any time from
          the{" "}
          <a href="/delete-account" className="font-medium text-primary underline underline-offset-2 hover:text-primary/80">
            account deletion page
          </a>
          . We may suspend or terminate accounts that violate these Terms.
        </p>
      </Section>

      <Section title="Disclaimers and limitation of liability">
        <p>
          SHUWA is provided "as is." We are not responsible for the quality
          of services provided by businesses on the platform. To the
          maximum extent permitted by law, SHUWA is not liable for
          indirect, incidental, or consequential damages arising from your
          use of the Service.
        </p>
      </Section>

      <Section title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. Continued use of
          SHUWA after changes take effect means you accept the updated
          Terms.
        </p>
      </Section>

      <div className="rounded-xl bg-muted p-5">
        <h2 className="font-montserrat text-lg font-semibold text-slate-900">Contact us</h2>
        <p className="mt-2 mb-0">
          Questions about these Terms? Email us at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </div>
    </>
  );
}
